export const CHAT_SESSION_INTENTS = ['general', 'document-search'] as const;

export type ChatSessionIntent = (typeof CHAT_SESSION_INTENTS)[number];

export const CHAT_GENERAL_PATH = '/chat';
export const CHAT_RAG_PATH = '/rag';
export const LEGACY_CHAT_RAG_PATH = '/chat/rag';

export type ChatLocationState = {
  chatIntent?: ChatSessionIntent;
  newChatKey?: string;
};

export type ParsedChatLocationState = {
  newChatKey?: string;
};

export function parseChatLocationState(state: unknown): ParsedChatLocationState {
  if (!state || typeof state !== 'object') {
    return {};
  }

  const record = state as Record<string, unknown>;

  return {
    newChatKey: typeof record.newChatKey === 'string' ? record.newChatKey : undefined,
  };
}

export function buildNewChatPath(intent: ChatSessionIntent) {
  return intent === 'document-search' ? CHAT_RAG_PATH : CHAT_GENERAL_PATH;
}

export function buildConversationPath(conversationId: string, intent: ChatSessionIntent) {
  return `${buildNewChatPath(intent)}/${conversationId}`;
}

export function buildNewChatLocationState(): ChatLocationState {
  return {
    newChatKey: crypto.randomUUID(),
  };
}

export function isRagSpacePath(pathname: string) {
  return pathname === CHAT_RAG_PATH || pathname.startsWith(`${CHAT_RAG_PATH}/`);
}

export function isChatExperiencePath(pathname: string) {
  return pathname === CHAT_GENERAL_PATH
    || pathname.startsWith(`${CHAT_GENERAL_PATH}/`)
    || isRagSpacePath(pathname);
}

export function resolveChatIntentFromPathname(pathname: string): ChatSessionIntent {
  if (isRagSpacePath(pathname) || pathname === LEGACY_CHAT_RAG_PATH || pathname === `${LEGACY_CHAT_RAG_PATH}/`) {
    return 'document-search';
  }

  return 'general';
}

export function resolveChatSessionIntent(input: {
  conversationId?: string;
  conversationRagEnabled?: boolean;
  pathname: string;
}): ChatSessionIntent {
  if (input.conversationId && typeof input.conversationRagEnabled === 'boolean') {
    return input.conversationRagEnabled ? 'document-search' : 'general';
  }

  return resolveChatIntentFromPathname(input.pathname);
}

export function extractChatConversationId(pathname: string) {
  const ragMatch = /^\/rag\/([^/]+)$/.exec(pathname);

  if (ragMatch) {
    return ragMatch[1];
  }

  const chatMatch = /^\/chat\/([^/]+)$/.exec(pathname);

  if (!chatMatch || chatMatch[1] === 'rag') {
    return undefined;
  }

  return chatMatch[1];
}

export function isChatNewRoute(pathname: string) {
  return pathname === CHAT_GENERAL_PATH
    || pathname === `${CHAT_GENERAL_PATH}/`
    || pathname === CHAT_RAG_PATH
    || pathname === `${CHAT_RAG_PATH}/`;
}

export function resolveNewChatRagEnabled(intent: ChatSessionIntent) {
  return intent === 'document-search';
}

export function resolveNewChatWebSearchEnabled(
  intent: ChatSessionIntent,
  defaultEnabled = true,
) {
  return intent === 'general' ? defaultEnabled : false;
}

export function buildRouteComposerKey(input: {
  chatIntent: ChatSessionIntent;
  conversationId?: string;
  newChatKey?: string;
}) {
  if (input.conversationId) {
    return input.conversationId;
  }

  return `new:${input.newChatKey ?? 'default'}:${input.chatIntent}`;
}

export function resolveSidebarChatNavActiveState(input: {
  activeConversation?: { ragEnabled: boolean } | null;
  pathname: string;
}) {
  const activeConversationId = extractChatConversationId(input.pathname);
  const isNewGeneralRoute = input.pathname === CHAT_GENERAL_PATH || input.pathname === `${CHAT_GENERAL_PATH}/`;
  const isNewRagRoute = input.pathname === CHAT_RAG_PATH || input.pathname === `${CHAT_RAG_PATH}/`;

  if (isRagSpacePath(input.pathname)) {
    return {
      isDocumentSearchActive: isNewRagRoute || Boolean(activeConversationId),
      isGeneralChatActive: false,
    };
  }

  return {
    isDocumentSearchActive: false,
    isGeneralChatActive: isNewGeneralRoute
      || (activeConversationId ? input.activeConversation?.ragEnabled === false : false),
  };
}
